import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import SaveAltIcon          from "@mui/icons-material/SaveAlt";
import RefreshIcon          from "@mui/icons-material/Refresh";
import AutoFixHighIcon      from "@mui/icons-material/AutoFixHigh";
import DeleteOutlineIcon    from "@mui/icons-material/DeleteOutline";
import CheckIcon            from "@mui/icons-material/Check";
import DomainAddIcon        from "@mui/icons-material/DomainAdd";
import ExpandMoreIcon       from "@mui/icons-material/ExpandMore";
import SettingsIcon         from "@mui/icons-material/Settings";
import AccessTimeIcon       from "@mui/icons-material/AccessTime";
import RecalculateIcon      from "@mui/icons-material/Calculate";
import { API_URL }          from "../../../../config";
import {
  useGetBanquetPricingMutation,
  useBulkSaveBanquetPricingMutation,
} from "../../../../Api/attoDeskApi";
import { useNotifier }      from "../../../../Core/Notifier";
import { useStyles }        from "../../../../theme/appStyles";
import { HALL_OPTIONS, DAY_TYPES, TIME_SLOTS } from "../constants";

// ── Types ──────────────────────────────────────────────────────────────────────
interface SlotRate {
  flatFee:        string;
  pricePerPerson: string;
  hourlyRate:     string;
  minHours:       string;
  hourlyDisabled: boolean;
}

type RateMap      = Record<string, Record<string, Record<string, SlotRate>>>;
type CustomSlotMap = Record<string, string[]>;

interface CustomHall { key: string; capacity: number; type: string; }

interface GlobalConfig {
  minBookingHours: number;
  turnoverFee:     number;
  fullDayCap:      boolean;
  amStart:   string;
  amEnd:     string;
  noonStart: string;
  noonEnd:   string;
  pmStart:   string;
  pmEnd:     string;
}

const DEFAULT_CONFIG: GlobalConfig = {
  minBookingHours: 2, turnoverFee: 300, fullDayCap: true,
  amStart: "08:00", amEnd: "12:00",
  noonStart: "12:00", noonEnd: "17:00",
  pmStart: "18:00", pmEnd: "01:00",
};

// ── Hall display config ────────────────────────────────────────────────────────
const HALL_COLORS: Record<string, string> = {
  Nova: "#1565c0", Titan: "#2e7d32", Lunar: "#e65100", Eclipse: "#6a1b9a",
  Outdoor: "#00695c", Second: "#00695c", Standing: "#37474f", Theatre: "#4a148c",
  Studio: "#bf360c", Office: "#bf360c", Board: "#bf360c", Half: "#bf360c", Full: "#bf360c",
};
const hallColor = (key: string) => HALL_COLORS[key.split(" ")[0]] ?? "#1a2f47";
const hallIcon  = (key: string) => {
  const m: Record<string, string> = {
    Nova:"N", Titan:"T", Lunar:"L", Eclipse:"E",
    Outdoor:"OC", Second:"SP", Standing:"ST", Theatre:"TH",
    Studio:"SR", Office:"OR", Board:"BR", Half:"HL", Full:"FL",
  };
  const f = key.split(" ")[0];
  return m[f] ?? key.slice(0, 2).toUpperCase();
};

// ── Hourly helpers ─────────────────────────────────────────────────────────────
const parseHours = (start: string, end: string): number => {
  const [sh, sm] = (start || "00:00").split(":").map(Number);
  const [eh, em] = (end   || "00:00").split(":").map(Number);
  let sMins = sh * 60 + sm;
  let eMins = eh * 60 + em;
  if (eMins <= sMins) eMins += 1440; // crosses midnight
  return Math.max(1, Math.round((eMins - sMins) / 60));
};

const getSlotHoursFromConfig = (slotVal: string, dayType: string, cfg: GlobalConfig): number => {
  if (slotVal === "AM")       return parseHours(cfg.amStart, cfg.amEnd);
  if (slotVal === "NOON")     return parseHours(cfg.noonStart, cfg.noonEnd);
  if (slotVal === "PM")       return parseHours(cfg.pmStart, cfg.pmEnd);
  if (slotVal === "FULL_DAY") return parseHours(cfg.amStart, cfg.pmEnd);
  return 5; // custom slots default
};

const fmt12 = (t: string): string => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const ampm   = h >= 12 ? "PM" : "AM";
  const h12    = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
};

const defaultHourlyDisabled = (dayType: string, slotVal: string): boolean => {
  if (dayType === "Holiday") return true;
  if ((dayType === "Sat" || dayType === "Sun") && (slotVal === "PM" || slotVal === "FULL_DAY")) return true;
  return false;
};

// ── Static helpers ─────────────────────────────────────────────────────────────
const STD_SLOT_VALUES = TIME_SLOTS.map(s => s.value);

const emptySlot = (dayType = "", slotVal = ""): SlotRate => ({
  flatFee: "0", pricePerPerson: "0",
  hourlyRate: "0", minHours: "2",
  hourlyDisabled: defaultHourlyDisabled(dayType, slotVal),
});

const buildEmptyMap = (): RateMap => {
  const map: RateMap = {};
  for (const hall of HALL_OPTIONS) {
    map[hall.key] = {};
    for (const day of DAY_TYPES) {
      map[hall.key][day] = {};
      for (const slot of TIME_SLOTS) map[hall.key][day][slot.value] = emptySlot(day, slot.value);
    }
  }
  return map;
};

const fmtPreview = (n: string | number): string => {
  const v = Number(n);
  return v ? "$" + v.toLocaleString("en-CA", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : "—";
};

const slotLabel = (val: string) =>
  val === "FULL_DAY" ? "Full" : val.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

// ── Component ──────────────────────────────────────────────────────────────────
const BasePricingTable = () => {
  const theme   = useTheme();
  const classes = useStyles();
  const { showMessage, showErrorMessage } = useNotifier();

  const [rates,        setRates]        = useState<RateMap>(buildEmptyMap());
  const [customSlots,  setCustomSlots]  = useState<CustomSlotMap>({});
  const [newSlotInput, setNewSlotInput] = useState<Record<string, string>>({});
  const [customHalls,  setCustomHalls]  = useState<CustomHall[]>([]);
  const [showNewHall,  setShowNewHall]  = useState(false);
  const [newHall,      setNewHall]      = useState({ key: "", capacity: "100", type: "single" });
  const [isDirty,      setIsDirty]      = useState(false);
  const [isFetching,   setIsFetching]   = useState(false);

  // Global settings
  const [globalConfig,     setGlobalConfig]     = useState<GlobalConfig>(DEFAULT_CONFIG);
  const [showGlobalConfig, setShowGlobalConfig] = useState(false);
  const [isSavingConfig,   setIsSavingConfig]   = useState(false);
  const [isConfigDirty,    setIsConfigDirty]    = useState(false);

  const allHalls = [
    ...HALL_OPTIONS.map(h => ({ key: h.key, capacity: h.capacity, type: h.type as string })),
    ...customHalls,
  ];

  const [getPricing]                        = useGetBanquetPricingMutation();
  const [bulkSave, { isLoading: isSaving }] = useBulkSaveBanquetPricingMutation();

  // ── Fetch config ────────────────────────────────────────────────────────────
  const fetchConfig = useCallback(async () => {
    try {
      const res  = await fetch(`${API_URL}/banquet/pricing/config`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
      });
      const data = await res.json();
      if (data.status && data.data) {
        const d = data.data;
        setGlobalConfig({
          minBookingHours: Number(d.minBookingHours) || 2,
          turnoverFee:     Number(d.turnoverFee)     || 300,
          fullDayCap:      Boolean(d.fullDayCap),
          amStart:   d.amStart   || "08:00",
          amEnd:     d.amEnd     || "12:00",
          noonStart: d.noonStart || "12:00",
          noonEnd:   d.noonEnd   || "17:00",
          pmStart:   d.pmStart   || "18:00",
          pmEnd:     d.pmEnd     || "01:00",
        });
      }
    } catch {}
  }, []);

  // ── Fetch pricing ───────────────────────────────────────────────────────────
  const fetchPricing = useCallback(async () => {
    setIsFetching(true);
    try {
      const res = (await getPricing({}).unwrap()) as any;
      if (res.status && Array.isArray(res.data)) {
        const map            = buildEmptyMap();
        const discovered: CustomSlotMap = {};
        const knownHallKeys  = new Set(HALL_OPTIONS.map(h => h.key));
        const discoveredHalls: CustomHall[] = [];

        for (const item of res.data) {
          const { hallKey: hk, dayType: dk, timeSlot: sk } = item;
          if (!hk || !dk || !sk) continue;

          if (!knownHallKeys.has(hk) && !discoveredHalls.find(h => h.key === hk)) {
            discoveredHalls.push({ key: hk, capacity: Number(item.hallCapacity) || 0, type: item.hallType ?? "single" });
          }
          if (!map[hk]) { map[hk] = {}; for (const day of DAY_TYPES) map[hk][day] = {}; }
          if (!map[hk][dk]) map[hk][dk] = {};

          map[hk][dk][sk] = {
            flatFee:        String(item.flatFee        ?? 0),
            pricePerPerson: String(item.pricePerPerson ?? 0),
            hourlyRate:     String(item.hourlyRate     ?? 0),
            minHours:       String(item.minHours       ?? 2),
            hourlyDisabled: Boolean(item.hourlyDisabled),
          };

          if (!(STD_SLOT_VALUES as readonly string[]).includes(sk)) {
            if (!discovered[hk]) discovered[hk] = [];
            if (!discovered[hk].includes(sk)) discovered[hk].push(sk);
          }
        }
        setRates(map);
        setCustomSlots(discovered);
        setCustomHalls(discoveredHalls);
        setIsDirty(false);
      }
    } catch (err: any) {
      showErrorMessage(err?.data?.message ?? "Failed to load pricing");
    } finally {
      setIsFetching(false);
    }
  }, [getPricing, showErrorMessage]);

  useEffect(() => { fetchPricing(); fetchConfig(); }, [fetchPricing, fetchConfig]);

  // ── Update a single cell ────────────────────────────────────────────────────
  const update = (hallKey: string, dayType: string, slotVal: string, field: keyof SlotRate, value: string | boolean) => {
    setRates(prev => ({
      ...prev,
      [hallKey]: {
        ...prev[hallKey],
        [dayType]: {
          ...prev[hallKey]?.[dayType],
          [slotVal]: { ...(prev[hallKey]?.[dayType]?.[slotVal] ?? emptySlot(dayType, slotVal)), [field]: value },
        },
      },
    }));
    setIsDirty(true);
  };

  // ── Recalculate hourly from current flatFee + configured time range ─────────
  const recalcHourly = (hallKey: string, dayType: string, slotVal: string) => {
    const flat = Number(rates[hallKey]?.[dayType]?.[slotVal]?.flatFee ?? 0);
    const hrs  = getSlotHoursFromConfig(slotVal, dayType, globalConfig);
    update(hallKey, dayType, slotVal, "hourlyRate", (!flat || !hrs) ? "0" : String(Math.round(flat / hrs)));
  };

  // ── Add / remove custom hall ────────────────────────────────────────────────
  const addCustomHall = () => {
    const key = newHall.key.trim();
    if (!key) return;
    if (allHalls.find(h => h.key.toLowerCase() === key.toLowerCase())) {
      showErrorMessage("A hall with that name already exists."); return;
    }
    const hall: CustomHall = { key, capacity: Number(newHall.capacity) || 0, type: newHall.type };
    setCustomHalls(prev => [...prev, hall]);
    setRates(prev => {
      const next = { ...prev, [key]: {} as Record<string, Record<string, SlotRate>> };
      for (const day of DAY_TYPES) { next[key][day] = {}; for (const slot of TIME_SLOTS) next[key][day][slot.value] = emptySlot(day, slot.value); }
      return next;
    });
    setNewHall({ key: "", capacity: "100", type: "single" });
    setShowNewHall(false);
    setIsDirty(true);
  };
  const removeCustomHall = (key: string) => {
    setCustomHalls(prev => prev.filter(h => h.key !== key));
    setRates(prev => { const n = { ...prev }; delete n[key]; return n; });
    setIsDirty(true);
  };

  // ── Add / remove custom slot ────────────────────────────────────────────────
  const addCustomSlot = (hallKey: string) => {
    const label  = (newSlotInput[hallKey] ?? "").trim();
    if (!label) return;
    const slotKey = label.toUpperCase().replace(/\s+/g, "_");
    setCustomSlots(prev => {
      const ex = prev[hallKey] ?? [];
      return ex.includes(slotKey) ? prev : { ...prev, [hallKey]: [...ex, slotKey] };
    });
    setRates(prev => {
      const next = { ...prev };
      for (const day of DAY_TYPES) {
        if (!next[hallKey]) next[hallKey] = {};
        if (!next[hallKey][day]) next[hallKey][day] = {};
        if (!next[hallKey][day][slotKey]) next[hallKey][day][slotKey] = emptySlot(day, slotKey);
      }
      return next;
    });
    setNewSlotInput(prev => ({ ...prev, [hallKey]: "" }));
    setIsDirty(true);
  };
  const removeCustomSlot = (hallKey: string, slotKey: string) => {
    setCustomSlots(prev => ({ ...prev, [hallKey]: (prev[hallKey] ?? []).filter(s => s !== slotKey) }));
    setIsDirty(true);
  };

  // ── Generate Defaults ───────────────────────────────────────────────────────
  const [isGenerating, setIsGenerating] = useState(false);
  const handleGenerateDefaults = async () => {
    setIsGenerating(true);
    try {
      const res  = await fetch(`${API_URL}/banquet/pricing/generate-defaults`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
      });
      const data = await res.json();
      if (data.status) { showMessage(data.message ?? "Defaults generated"); fetchPricing(); }
      else showErrorMessage(data.message ?? "Failed");
    } catch { showErrorMessage("Failed to generate defaults"); }
    setIsGenerating(false);
  };

  // ── Save global config ──────────────────────────────────────────────────────
  const handleSaveConfig = async () => {
    setIsSavingConfig(true);
    try {
      const res  = await fetch(`${API_URL}/banquet/pricing/config`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
        body: JSON.stringify(globalConfig),
      });
      const data = await res.json();
      if (data.status) { showMessage(data.message ?? "Config saved"); setIsConfigDirty(false); }
      else showErrorMessage(data.message ?? "Failed");
    } catch { showErrorMessage("Failed to save config"); }
    setIsSavingConfig(false);
  };

  // ── Save All pricing ────────────────────────────────────────────────────────
  const handleSave = async () => {
    const rules: any[] = [];
    for (const hall of allHalls) {
      const allSlots = [...STD_SLOT_VALUES, ...(customSlots[hall.key] ?? [])];
      for (const day of DAY_TYPES) {
        for (const slotVal of allSlots) {
          const r = rates[hall.key]?.[day]?.[slotVal];
          if (!r) continue;
          rules.push({
            hallKey: hall.key, hallCapacity: hall.capacity, hallType: hall.type,
            dayType: day, timeSlot: slotVal,
            flatFee:        Number(r.flatFee)        || 0,
            pricePerPerson: Number(r.pricePerPerson) || 0,
            hourlyRate:     Number(r.hourlyRate)     || 0,
            minHours:       Number(r.minHours)       || 2,
            hourlyDisabled: r.hourlyDisabled,
          });
        }
      }
    }
    try {
      const res = (await bulkSave({ rules }).unwrap()) as any;
      if (res.status) { showMessage(res.message ?? "Pricing saved"); setIsDirty(false); }
      else showErrorMessage(res.message ?? "Save failed");
    } catch (err: any) { showErrorMessage(err?.data?.message ?? err?.message ?? "Save failed"); }
  };

  const inputSx = {
    "& .MuiInputBase-root": { fontSize: 12, fontWeight: 600 },
    "& input": { py: "4px", px: "5px", fontSize: 11 },
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>

      {/* ── Global Hourly Booking Rules ── */}
      <Paper variant="outlined" sx={{ mb: 2.5, borderRadius: 2, overflow: "hidden", borderColor: "#e2e8f0" }}>
        <Box
          onClick={() => setShowGlobalConfig(v => !v)}
          sx={{
            px: 2, py: 1.5, display: "flex", alignItems: "center", gap: 1.5,
            cursor: "pointer", bgcolor: "#f8fafc",
            "&:hover": { bgcolor: "#f1f5f9" },
            borderBottom: showGlobalConfig ? "1px solid #e2e8f0" : "none",
          }}
        >
          <SettingsIcon sx={{ fontSize: 18, color: "#64748b" }} />
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#1e293b", flex: 1 }}>
            Hourly Booking Rules
          </Typography>
          {isConfigDirty && (
            <Box sx={{ px: 1, py: 0.2, borderRadius: "8px", bgcolor: "#fff7ed", border: "1px solid #fed7aa", fontSize: 10, fontWeight: 700, color: "#c2410c" }}>
              unsaved
            </Box>
          )}
          <ExpandMoreIcon sx={{ fontSize: 18, color: "#94a3b8", transform: showGlobalConfig ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
        </Box>

        <Collapse in={showGlobalConfig}>
          <Box sx={{ px: 2.5, py: 2 }}>

            {/* ── Time Range Row ── */}
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.07em", mb: 1.5 }}>
              Slot Time Ranges
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2.5 }}>
              {([
                { label: "AM",   startKey: "amStart",   endKey: "amEnd",   color: "#2563eb" },
                { label: "Noon", startKey: "noonStart", endKey: "noonEnd", color: "#7c3aed" },
                { label: "PM",   startKey: "pmStart",   endKey: "pmEnd",   color: "#ea580c" },
              ] as const).map(({ label, startKey, endKey, color }) => {
                const hrs = parseHours(globalConfig[startKey], globalConfig[endKey]);
                return (
                  <Paper key={label} variant="outlined" sx={{ p: 1.5, borderRadius: 2, borderColor: `${color}40`, bgcolor: `${color}06`, minWidth: 200 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 1 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: color }} />
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color }}>{label}</Typography>
                      <Box sx={{ ml: "auto", px: 0.8, py: 0.1, borderRadius: "8px", bgcolor: `${color}15`, border: `1px solid ${color}30`, fontSize: 10, fontWeight: 700, color }}>
                        {hrs} hr{hrs !== 1 ? "s" : ""}
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <TextField
                        label="Start"
                        type="time"
                        size="small"
                        value={globalConfig[startKey]}
                        onChange={e => { setGlobalConfig(p => ({ ...p, [startKey]: e.target.value })); setIsConfigDirty(true); }}
                        InputLabelProps={{ shrink: true }}
                        sx={{ flex: 1, "& input": { fontSize: 12 } }}
                      />
                      <Typography sx={{ fontSize: 11, color: "text.disabled" }}>—</Typography>
                      <TextField
                        label="End"
                        type="time"
                        size="small"
                        value={globalConfig[endKey]}
                        onChange={e => { setGlobalConfig(p => ({ ...p, [endKey]: e.target.value })); setIsConfigDirty(true); }}
                        InputLabelProps={{ shrink: true }}
                        sx={{ flex: 1, "& input": { fontSize: 12 } }}
                      />
                    </Box>
                    <Typography sx={{ fontSize: 10, color: "text.disabled", mt: 0.6, textAlign: "center" }}>
                      {fmt12(globalConfig[startKey])} – {fmt12(globalConfig[endKey])}
                    </Typography>
                  </Paper>
                );
              })}

              {/* Full Day derived */}
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, borderColor: "#64748b40", bgcolor: "#f8fafc", minWidth: 200 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 1 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#475569" }} />
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#475569" }}>Full Day</Typography>
                  <Box sx={{ ml: "auto", px: 0.8, py: 0.1, borderRadius: "8px", bgcolor: "#f1f5f9", border: "1px solid #cbd5e1", fontSize: 10, fontWeight: 700, color: "#64748b" }}>
                    {parseHours(globalConfig.amStart, globalConfig.pmEnd)} hrs
                  </Box>
                </Box>
                <Typography sx={{ fontSize: 11, color: "#64748b", textAlign: "center", py: 0.8 }}>
                  {fmt12(globalConfig.amStart)} – {fmt12(globalConfig.pmEnd)}
                </Typography>
                <Typography sx={{ fontSize: 10, color: "text.disabled", textAlign: "center" }}>
                  Derived from AM start → PM end
                </Typography>
              </Paper>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* ── Booking Rules Row ── */}
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.07em", mb: 1.5 }}>
              Booking Rules
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, alignItems: "flex-end" }}>
              <TextField
                label="Global Min Booking Hours"
                type="number" size="small"
                value={globalConfig.minBookingHours}
                onChange={e => { setGlobalConfig(p => ({ ...p, minBookingHours: Number(e.target.value) })); setIsConfigDirty(true); }}
                inputProps={{ min: 1, max: 24 }}
                sx={{ width: 200 }}
                helperText="Applies to all halls unless overridden per slot"
              />
              <TextField
                label="Turnover Fee Between Events ($)"
                type="number" size="small"
                value={globalConfig.turnoverFee}
                onChange={e => { setGlobalConfig(p => ({ ...p, turnoverFee: Number(e.target.value) })); setIsConfigDirty(true); }}
                inputProps={{ min: 0 }}
                sx={{ width: 230 }}
                helperText="Fee applied between back-to-back bookings"
              />
              <Box>
                <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.5 }}>Full-Day Price Cap</Typography>
                <FormControlLabel
                  control={<Switch size="small" checked={globalConfig.fullDayCap} onChange={e => { setGlobalConfig(p => ({ ...p, fullDayCap: e.target.checked })); setIsConfigDirty(true); }} />}
                  label={<Typography sx={{ fontSize: 12, color: globalConfig.fullDayCap ? "success.main" : "text.secondary", fontWeight: 600 }}>{globalConfig.fullDayCap ? "Enabled" : "Disabled"}</Typography>}
                />
                <Typography sx={{ fontSize: 10, color: "text.secondary" }}>Stacked hourly bookings capped at Full Day price</Typography>
              </Box>
              <Button
                size="small" variant="contained"
                onClick={handleSaveConfig}
                disabled={!isConfigDirty || isSavingConfig}
                startIcon={isSavingConfig ? <CircularProgress size={13} color="inherit" /> : <SaveAltIcon fontSize="small" />}
                sx={{ height: 36, textTransform: "none", fontWeight: 600 }}
              >
                {isSavingConfig ? "Saving…" : "Save Rules"}
              </Button>
            </Box>
          </Box>
        </Collapse>
      </Paper>

      {/* ── Toolbar ── */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Set flat fee, per-person rate, and hourly pricing for each hall and time slot.
          {isDirty && <Typography component="span" variant="body2" sx={{ color: "warning.main", ml: 1 }}>• unsaved changes</Typography>}
        </Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            size="small" variant="outlined"
            startIcon={<DomainAddIcon fontSize="small" />}
            endIcon={<ExpandMoreIcon fontSize="small" sx={{ transform: showNewHall ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />}
            onClick={() => setShowNewHall(v => !v)}
            sx={{ textTransform: "none", fontSize: 12, fontWeight: 600, borderColor: "#6366f1", color: "#4338ca", "&:hover": { bgcolor: "#eef2ff" } }}
          >
            Add New Hall / Venue Type
          </Button>
          <Tooltip title="Auto-fill empty (0.00) fields with smart defaults">
            <span>
              <Button
                size="small" variant="outlined"
                onClick={handleGenerateDefaults}
                disabled={isGenerating || isFetching}
                startIcon={isGenerating ? <CircularProgress size={14} /> : <AutoFixHighIcon fontSize="small" />}
                sx={{ textTransform: "none", fontSize: 12, fontWeight: 600, borderColor: "#D6B98C", color: "#5A4633", "&:hover": { bgcolor: "#F5EDE4" } }}
              >
                {isGenerating ? "Generating…" : "Generate Defaults"}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Reload from server">
            <span>
              <Button size="small" variant="outlined" className={classes.addButton} onClick={fetchPricing} disabled={isFetching} sx={{ minWidth: 0, px: 1 }}>
                <RefreshIcon fontSize="small" />
              </Button>
            </span>
          </Tooltip>
          <Button
            size="small" variant="contained" className={classes.addButton}
            onClick={handleSave} disabled={isSaving || isFetching}
            startIcon={isSaving ? <CircularProgress size={14} color="inherit" /> : <SaveAltIcon />}
          >
            {isSaving ? "Saving…" : "Save All"}
          </Button>
        </Box>
      </Box>

      {isFetching && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {/* ── New Hall Form ── */}
      <Collapse in={showNewHall}>
        <Paper variant="outlined" sx={{ mb: 2.5, p: 2, borderRadius: 2, borderColor: "#6366f1", bgcolor: "#fafafe" }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#4338ca", mb: 1.5 }}>Create New Hall / Venue Type</Typography>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "flex-end" }}>
            <TextField label="Hall Name" size="small" value={newHall.key} onChange={e => setNewHall(p => ({ ...p, key: e.target.value }))} placeholder="e.g. Crystal Hall" sx={{ width: 220 }} onKeyDown={e => { if (e.key === "Enter") addCustomHall(); }} />
            <TextField label="Max Capacity" size="small" type="number" value={newHall.capacity} onChange={e => setNewHall(p => ({ ...p, capacity: e.target.value }))} inputProps={{ min: 1 }} sx={{ width: 130 }} />
            <Box>
              <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.4 }}>Type</Typography>
              <Select size="small" value={newHall.type} onChange={e => setNewHall(p => ({ ...p, type: e.target.value }))} sx={{ width: 140, fontSize: 13 }}>
                {["single", "combo", "outdoor", "special", "room"].map(t => <MenuItem key={t} value={t} sx={{ fontSize: 13 }}>{t}</MenuItem>)}
              </Select>
            </Box>
            <Button variant="contained" size="small" onClick={addCustomHall} disabled={!newHall.key.trim()} startIcon={<CheckIcon fontSize="small" />} sx={{ bgcolor: "#6366f1", "&:hover": { bgcolor: "#4338ca" }, textTransform: "none", fontWeight: 600, height: 40 }}>Add Hall</Button>
            <Button variant="text" size="small" onClick={() => setShowNewHall(false)} sx={{ textTransform: "none", color: "text.secondary", height: 40 }}>Cancel</Button>
          </Box>
        </Paper>
      </Collapse>

      {/* ── Hall sections ── */}
      {allHalls.map((hall) => {
        const color        = hallColor(hall.key);
        const icon         = hallIcon(hall.key);
        const hallCustoms  = customSlots[hall.key] ?? [];
        const allSlots     = [...STD_SLOT_VALUES, ...hallCustoms];
        const isCustomHall = !!customHalls.find(h => h.key === hall.key);

        return (
          <Box key={hall.key} sx={{ mb: 4 }}>
            {/* Hall heading */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
              <Box sx={{ width: 36, height: 36, borderRadius: "9px", bgcolor: color, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 13, fontWeight: 700, flexShrink: 0 }}>{icon}</Box>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{hall.key}</Typography>
                  {isCustomHall && <Box sx={{ px: 0.8, py: 0.1, borderRadius: "10px", bgcolor: "#eef2ff", border: "1px solid #c7d2fe", fontSize: 10, fontWeight: 700, color: "#4338ca" }}>custom</Box>}
                </Box>
                <Typography variant="caption" color="text.secondary">{hall.capacity} pax · {hall.type}</Typography>
              </Box>
              {isCustomHall && (
                <Tooltip title="Remove this custom hall">
                  <IconButton size="small" onClick={() => removeCustomHall(hall.key)} sx={{ color: "text.disabled", "&:hover": { color: "error.main" } }}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {/* Add custom slot */}
              <TextField
                size="small"
                placeholder="Add set… e.g. Projector View"
                value={newSlotInput[hall.key] ?? ""}
                onChange={e => setNewSlotInput(prev => ({ ...prev, [hall.key]: e.target.value }))}
                onKeyDown={e => { if (e.key === "Enter") addCustomSlot(hall.key); }}
                sx={{ width: 200, "& input": { fontSize: 11, py: "6px" } }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Add pricing set">
                        <IconButton size="small" onClick={() => addCustomSlot(hall.key)} disabled={!(newSlotInput[hall.key] ?? "").trim()} sx={{ color }}>
                          <CheckIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Custom slot chips */}
            {hallCustoms.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
                {hallCustoms.map(sk => (
                  <Box key={sk} sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, px: 1.2, py: 0.3, borderRadius: "20px", bgcolor: `${color}18`, border: `1px solid ${color}40`, fontSize: 11, fontWeight: 600, color }}>
                    {slotLabel(sk)}
                    <Tooltip title="Remove this custom set">
                      <IconButton size="small" onClick={() => removeCustomSlot(hall.key, sk)} sx={{ width: 16, height: 16, ml: 0.3, color: "text.disabled", "&:hover": { color: "error.main" } }}>
                        <DeleteOutlineIcon sx={{ fontSize: 13 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
              </Box>
            )}

            {/* Day-type cards */}
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(3,1fr)", lg: "repeat(5,1fr)" }, gap: 1.5 }}>
              {DAY_TYPES.map((day) => (
                <Paper key={day} variant="outlined" sx={{ overflow: "hidden", borderRadius: 2, "&:hover": { boxShadow: theme.shadows[2] } }}>
                  {/* Card header */}
                  <Box sx={{ px: 1.5, py: 0.8, bgcolor: "action.hover", borderBottom: `1px solid ${theme.palette.divider}`, display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase" }}>{day}</Typography>
                  </Box>

                  <Box sx={{ p: 1 }}>
                    {/* Column headers */}
                    <Box sx={{ display: "grid", gridTemplateColumns: "52px 1fr 1fr", gap: 0.4, mb: 0.5 }}>
                      <Box />
                      <Typography variant="caption" sx={{ fontSize: 9, color: "text.secondary", textAlign: "center" }}>Flat ($)</Typography>
                      <Typography variant="caption" sx={{ fontSize: 9, color: "text.secondary", textAlign: "center" }}>/person</Typography>
                    </Box>

                    {allSlots.map((slotVal, sIdx) => {
                      const r        = rates[hall.key]?.[day]?.[slotVal] ?? emptySlot(day, slotVal);
                      const isCustom = !(STD_SLOT_VALUES as readonly string[]).includes(slotVal);

                      return (
                        <React.Fragment key={slotVal}>
                          {sIdx > 0 && <Divider sx={{ my: 0.5 }} />}
                          {isCustom && sIdx === STD_SLOT_VALUES.length && (
                            <Box sx={{ my: 0.5, mx: -1, px: 1, py: 0.3, bgcolor: `${color}10`, borderTop: `1px dashed ${color}40`, borderBottom: `1px dashed ${color}40` }}>
                              <Typography sx={{ fontSize: 9, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: "0.08em" }}>Add-ons</Typography>
                            </Box>
                          )}

                          {/* Flat + PP row */}
                          <Box sx={{ display: "grid", gridTemplateColumns: "52px 1fr 1fr", gap: 0.4, alignItems: "center" }}>
                            <Typography variant="caption" sx={{ fontSize: 9, fontWeight: 600, color: isCustom ? color : "text.secondary", textTransform: "uppercase", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {slotLabel(slotVal)}
                            </Typography>
                            <TextField size="small" type="number" value={r.flatFee}
                              onChange={e => update(hall.key, day, slotVal, "flatFee", e.target.value)}
                              inputProps={{ min: 0, step: 1, style: { textAlign: "right" } }} sx={inputSx} />
                            <TextField size="small" type="number" value={r.pricePerPerson}
                              onChange={e => update(hall.key, day, slotVal, "pricePerPerson", e.target.value)}
                              inputProps={{ min: 0, step: 0.5, style: { textAlign: "right" } }} sx={inputSx} />
                          </Box>

                          {/* Hourly Rate + Min Hours row */}
                          <Box sx={{ display: "grid", gridTemplateColumns: "52px 1fr 1fr", gap: 0.4, alignItems: "center", mt: 0.4 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.2 }}>
                              <AccessTimeIcon sx={{ fontSize: 10, color: "#6366f1" }} />
                              <Typography variant="caption" sx={{ fontSize: 8.5, fontWeight: 600, color: "#6366f1", textTransform: "uppercase" }}>/hr</Typography>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                              <TextField size="small" type="number" value={r.hourlyRate}
                                onChange={e => update(hall.key, day, slotVal, "hourlyRate", e.target.value)}
                                inputProps={{ min: 0, step: 1, style: { textAlign: "right" } }}
                                sx={{ ...inputSx, "& .MuiInputBase-root": { fontSize: 11, fontWeight: 600, bgcolor: "#eef2ff" } }} />
                              <Tooltip title="Recalculate from flat fee">
                                <IconButton size="small" onClick={() => recalcHourly(hall.key, day, slotVal)} sx={{ width: 18, height: 18, color: "#6366f1", flexShrink: 0, p: 0 }}>
                                  <RecalculateIcon sx={{ fontSize: 13 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                            <Tooltip title="Min booking hours (1–12)">
                              <TextField size="small" type="number" value={r.minHours}
                                onChange={e => update(hall.key, day, slotVal, "minHours", e.target.value)}
                                inputProps={{ min: 1, max: 12, step: 1, style: { textAlign: "right" } }}
                                placeholder="hrs"
                                sx={{ ...inputSx, "& .MuiInputBase-root": { fontSize: 11, fontWeight: 600, bgcolor: "#f0fdf4" } }} />
                            </Tooltip>
                          </Box>

                          {/* Hourly disabled checkbox */}
                          <FormControlLabel
                            sx={{ ml: 0, mt: 0.3 }}
                            control={
                              <Checkbox
                                size="small"
                                checked={r.hourlyDisabled}
                                onChange={e => update(hall.key, day, slotVal, "hourlyDisabled", e.target.checked)}
                                sx={{ p: 0.3, color: "#94a3b8", "&.Mui-checked": { color: "#ef4444" } }}
                              />
                            }
                            label={
                              <Typography sx={{ fontSize: 9.5, color: r.hourlyDisabled ? "#ef4444" : "text.disabled", fontWeight: r.hourlyDisabled ? 600 : 400 }}>
                                Hourly booking disabled
                              </Typography>
                            }
                          />
                        </React.Fragment>
                      );
                    })}
                  </Box>
                </Paper>
              ))}
            </Box>
          </Box>
        );
      })}

      {/* ── Rate Preview Table ── */}
      <Box sx={{ mt: 2, mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Rate Preview</Typography>
        <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1, overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: "primary.main" }}>
                {["Hall", "Capacity", "Day",
                  "AM Flat", "AM /pp", "AM /hr",
                  "Noon Flat", "Noon /pp", "Noon /hr",
                  "PM Flat", "PM /pp", "PM /hr",
                  "Full Flat", "Full /pp", "Full /hr"].map(h => (
                  <TableCell key={h} sx={{ color: "primary.contrastText", fontWeight: 700, fontSize: 10, py: "7px", px: "8px", whiteSpace: "nowrap" }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {allHalls.flatMap(hall =>
                DAY_TYPES.map((day, dIdx) => {
                  const slots = ["AM", "NOON", "PM", "FULL_DAY"].map(sk => rates[hall.key]?.[day]?.[sk] ?? emptySlot(day, sk));
                  return (
                    <TableRow key={`${hall.key}-${day}`} sx={{ "&:last-child td": { border: 0 }, "&:hover td": { bgcolor: "action.hover" } }}>
                      {dIdx === 0 && <TableCell rowSpan={DAY_TYPES.length} sx={{ fontWeight: 700, fontSize: 11, px: "8px", verticalAlign: "top", pt: "8px", borderRight: `3px solid ${hallColor(hall.key)}` }}>{hall.key}</TableCell>}
                      {dIdx === 0 && <TableCell rowSpan={DAY_TYPES.length} sx={{ fontSize: 11, px: "8px", color: "text.secondary", verticalAlign: "top", pt: "8px" }}>{hall.capacity}</TableCell>}
                      <TableCell sx={{ fontSize: 10, px: "8px", py: "5px" }}>
                        <Box component="span" sx={{ display: "inline-block", px: "6px", py: "1px", borderRadius: "20px", fontSize: 9.5, fontWeight: 600, bgcolor: "rgba(237,108,2,0.08)", color: "warning.dark", border: "1px solid rgba(237,108,2,0.3)" }}>{day}</Box>
                      </TableCell>
                      {slots.flatMap((r, idx) => [
                        <TableCell key={idx+"-f"} sx={{ fontSize: 12, fontWeight: 700, px: "8px", py: "5px" }}>{fmtPreview(r.flatFee)}</TableCell>,
                        <TableCell key={idx+"-p"} sx={{ fontSize: 11, px: "8px", py: "5px", color: "success.main", fontWeight: 600 }}>{fmtPreview(r.pricePerPerson)}</TableCell>,
                        <TableCell key={idx+"-h"} sx={{ fontSize: 11, px: "8px", py: "5px", color: r.hourlyDisabled ? "text.disabled" : "#6366f1", fontWeight: 600 }}>
                          {r.hourlyDisabled ? <span title="Hourly disabled">—</span> : fmtPreview(r.hourlyRate)}
                        </TableCell>,
                      ])}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {isDirty && (
        <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: "block" }}>
          You have unsaved changes. Click <strong>Save All</strong> to apply.
        </Typography>
      )}
    </Box>
  );
};

export default BasePricingTable;
